import React from "react";
import cn from "classnames";
import styles from "./dashboard-canvas-header.module.scss";
import { Dashboard } from "@src/types/dashboards";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { useI18N } from "@src/language/language-context";

interface DashboardCanvasHeaderProps {
  dashboard: Dashboard;
  // Optional because the read-only viewer doesn't expose publish. When
  // `readOnly` is true the button is not rendered; otherwise the prop
  // is required (kept optional at the type level to keep one-and-the-
  // same component usable in both contexts without a wrapper).
  onPublish?: (id: string) => void;
  /** When true, render the header without editor controls (the
   *  publish button). Name + draft/published badge + last-updated
   *  stay visible — they're "public" info any viewer should see.
   *  Default `false` so existing callers don't change behaviour. */
  readOnly?: boolean;
  /** Optional explicit background override forwarded to the
   *  `<SubHeader>` atom. When unset, the atom reads
   *  `var(--color-cp-subheader-bg)` from the central-panel
   *  theme cascade, falling back to `colors.$backgroundLight`.
   *  Use this prop for tests / Storybook where you want to
   *  paint a specific color off the theme chain. */
  background?: React.CSSProperties["background"];
}

const cssClassName = "dashboard-canvas-header";

export const DashboardCanvasHeader = ({
  dashboard,
  onPublish,
  background,
  readOnly = false,
}: DashboardCanvasHeaderProps) => {
  const i18n = useI18N();
  const isDraft = dashboard.status === "draft";

  const texts = {
    draft: i18n("modules.dashboardCard.draft"),
    published: i18n("modules.dashboardCard.published"),
    publish: i18n("pages.dashboardsPage.publish"),
    lastUpdated: i18n("pages.dashboardsPage.lastUpdated"),
  };

  const parsedUpdatedAt = new Date(dashboard.updated_at);
  const lastUpdated = Number.isNaN(parsedUpdatedAt.getTime())
    ? ""
    : parsedUpdatedAt.toLocaleString();

  return (
    <SubHeader variant={"less-padding"} background={background}>
      <div className={styles[`${cssClassName}__grid`]}>
        <div className={styles[`${cssClassName}__left`]}>
          <span className={styles[`${cssClassName}__name`]}>
            {dashboard.name}
          </span>
          {dashboard.description && (
            <span className={styles[`${cssClassName}__description`]}>
              {dashboard.description}
            </span>
          )}
        </div>

        <span className={styles[`${cssClassName}__timestamp`]}>
          {lastUpdated ? `${texts.lastUpdated}: ${lastUpdated}` : ""}
        </span>

        <div className={styles[`${cssClassName}__right`]}>
          <span
            className={cn(styles[`${cssClassName}__status`], {
              [styles[`${cssClassName}__status--draft`]]: isDraft,
              [styles[`${cssClassName}__status--published`]]: !isDraft,
            })}
          >
            {isDraft ? texts.draft : texts.published}
          </span>
          {isDraft && !readOnly && (
            <button
              className={styles[`${cssClassName}__publish`]}
              onClick={() => onPublish?.(dashboard.id)}
            >
              {texts.publish}
            </button>
          )}
        </div>
      </div>
    </SubHeader>
  );
};
